import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { referenceEqual } from "../equality";
import type { MemorySliceStore } from "../MemorySliceStore";
import type { MemorySliceDescriptor } from "../types";
import { useLayoutRef } from "./useLayoutRef";

/**
 * Subscribes to a derived portion of a memory slice to limit re-renders.
 *
 * @typeParam TValue Full slice value type.
 * @typeParam TSelected Projected value type.
 * @param store Memory slice store instance.
 * @param slice Slice descriptor.
 * @param selector Projection from full slice value; should be stable (`useCallback`) when non-trivial.
 * @param isEqual Equality for selected values (defaults to `Object.is`).
 * @returns Selected value; re-renders only when the selection changes per `isEqual`.
 */
export const useMemorySliceSelector = <TValue, TSelected>(
    store: MemorySliceStore,
    slice: MemorySliceDescriptor<TValue>,
    selector: ( value: TValue ) => TSelected,
    isEqual: ( left: TSelected, right: TSelected ) => boolean = referenceEqual,
): TSelected => {
    const storeRef = useLayoutRef( store );
    const sliceRef = useLayoutRef( slice );
    const selectorRef = useLayoutRef( selector );
    const isEqualRef = useLayoutRef( isEqual );

    const selectedRef = useRef<TSelected>( selector( store.getSnapshot( slice ) ) );

    useEffect( () => {
        const nextSelection = selectorRef.current( storeRef.current.getSnapshot( sliceRef.current ) );

        if ( !isEqualRef.current( selectedRef.current, nextSelection ) ) {
            selectedRef.current = nextSelection;
        }
    }, [ isEqualRef, selectorRef, sliceRef, store, slice, selector, isEqual, storeRef ] );

    const subscribe = useCallback( ( onChange: () => void ) => {
        return storeRef.current.subscribe( sliceRef.current, () => {
            const nextSelection = selectorRef.current( storeRef.current.getSnapshot( sliceRef.current ) );

            if ( !isEqualRef.current( selectedRef.current, nextSelection ) ) {
                selectedRef.current = nextSelection;
                onChange();
            }
        } );
    }, [ isEqualRef, selectorRef, sliceRef, storeRef ] );

    const getSelection = useCallback( (): TSelected => {
        return selectedRef.current;
    }, [] );

    return useSyncExternalStore( subscribe, getSelection, getSelection );
};
